<template>
    <InfoBox title="SFDP Details">
    
    <div class="table-responsive p-2">
      <!-- Main SFDP Info -->
      <h6 class="font-semibold mb-2">Header Information</h6>
      <table class="font-mono text-sm text-left mb-4">
        <tbody>
          <tr>
            <th>SFDP Revision</th>
            <td class="pl-2 pr-3">{{ sfdp.majorRevision }}.{{ sfdp.minorRevision }}</td>
            <th class="border-l border-grey border-opacity-25 pr-2 pl-3">Parameter Headers</th>
            <td>{{ sfdp.nph + 1 }}</td>
          </tr>
          <tr>
            <th>Access Protocol</th>
            <td class="pl-2 pr-3">0x{{ formatHex(sfdp.accessProtocol) }}</td>
            <th class="border-l border-grey border-opacity-25 pr-2 pl-3">Signature</th>
            <td>{{ formatSignature(sfdp.signature) }}</td>
          </tr>
        </tbody>
      </table>

      <!-- Flash Properties -->
      <h6 class="font-semibold mb-2">Flash Properties</h6>
      <table class="font-mono text-sm text-left mb-4">
        <tbody>
          <tr>
            <th>Density</th>
            <td class="pl-2 pr-3">{{ sfdp.densityHuman || formatDensity(sfdp.density) }}</td>
            <th class="border-l border-grey border-opacity-25 pr-2 pl-3">Address Bytes</th>
            <td>{{ sfdp.addressBytes }}-byte{{ sfdp.bfpt?.supports4ByteAddress ? ' (4-byte capable)' : '' }}</td>
          </tr>
          <tr>
            <th>Page Size</th>
            <td class="pl-2 pr-3">{{ sfdp.pageSize || 256 }} bytes</td>
            <th class="border-l border-grey border-opacity-25 pr-2 pl-3">Write Granularity</th>
            <td>{{ sfdp.writeGranularity ? '64+ bytes' : '1 byte' }}</td>
          </tr>
        </tbody>
      </table>

      <!-- Erase Types -->
      <h6 class="font-semibold mb-2">Erase Commands</h6>
      <table class="font-mono text-sm text-left mb-4" v-if="sfdp.eraseTypes && sfdp.eraseTypes.length">
        <thead>
          <tr class="border-b border-grey border-opacity-25">
            <th class="pr-4">Type</th>
            <th class="pr-4">Size</th>
            <th class="pr-4">Opcode</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="et in sfdp.eraseTypes" :key="et.typeNumber">
            <td class="pr-4">Type {{ et.typeNumber }}</td>
            <td class="pr-4">{{ et.sizeHuman }}</td>
            <td class="pr-4">{{ et.opcodeHex }}</td>
          </tr>
        </tbody>
      </table>
      <p v-else class="text-sm text-gray-500 mb-4">No erase types found</p>

      <!-- Features -->
      <h6 class="font-semibold mb-2">Supported Features</h6>
      <div class="flex flex-wrap gap-2 mb-4">
        <span v-if="sfdp.bfpt?.supportDTR" class="badge">DTR Clocking</span>
        <span v-if="sfdp.bfpt?.support112FastRead" class="badge">1-1-2 Fast Read</span>
        <span v-if="sfdp.bfpt?.support122FastRead" class="badge">1-2-2 Fast Read</span>
        <span v-if="sfdp.bfpt?.support114FastRead" class="badge">1-1-4 Fast Read</span>
        <span v-if="sfdp.bfpt?.support144FastRead" class="badge">1-4-4 Fast Read</span>
        <span v-if="sfdp.hasOctalDDRSupport && sfdp.hasOctalDDRSupport()" class="badge">Octal DDR</span>
        <span v-if="sfdp.hasXSPIProfile && sfdp.hasXSPIProfile()" class="badge">xSPI</span>
        <span v-if="!hasAnyFeature" class="text-sm text-gray-500">Standard SPI only</span>
      </div>

      <!-- Parameter Tables -->
      <h6 class="font-semibold mb-2">Parameter Tables</h6>
      <table class="font-mono text-sm text-left" v-if="sfdp.parameterHeaders && sfdp.parameterHeaders.length">
        <thead>
          <tr class="border-b border-grey border-opacity-25">
            <th class="pr-4">ID</th>
            <th class="pr-4">Name</th>
            <th class="pr-4">Rev</th>
            <th class="pr-4">Size</th>
            <th class="pr-4">Offset</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="ph in sfdp.parameterHeaders" :key="ph.index">
            <td class="pr-4">0x{{ formatHex(ph.parameterId, 4) }}</td>
            <td class="pr-4 max-w-xs truncate" :title="ph.name">{{ shortenName(ph.name) }}</td>
            <td class="pr-4">{{ ph.majorRevision }}.{{ ph.minorRevision }}</td>
            <td class="pr-4">{{ ph.lengthBytes }}B</td>
            <td class="pr-4">0x{{ formatHex(ph.tablePointer, 4) }}</td>
          </tr>
        </tbody>
      </table>

      <!-- Legacy fields (collapsible) -->
      <details class="mt-4">
        <summary class="cursor-pointer text-sm text-gray-600 hover:text-gray-800">Show Raw Values</summary>
        <table class="font-mono text-sm text-left mt-2">
          <tbody>
            <tr>
              <th>BFPT Revision</th>
              <td class="pl-2 pr-3">{{ sfdp.parameterMajorRevision }}.{{ sfdp.parameterMinorRevision }}</td>
              <th class="border-l border-grey border-opacity-25 pr-2 pl-3">BFPT Length</th>
              <td>{{ sfdp.parameterLength }} bytes</td>
            </tr>
            <tr>
              <th>BFPT Pointer</th>
              <td class="pl-2 pr-3">0x{{ formatHex(sfdp.ptp, 4) }}</td>
              <th class="border-l border-grey border-opacity-25 pr-2 pl-3">4K Erase Opcode</th>
              <td>0x{{ formatHex(sfdp.eraseOpcode4k) }}</td>
            </tr>
            <tr>
              <th>Address Bytes Raw</th>
              <td class="pl-2 pr-3">0b{{ sfdp.addressBytesRaw?.toString(2).padStart(2, '0') }}</td>
              <th class="border-l border-grey border-opacity-25 pr-2 pl-3">Block/Sector Erase</th>
              <td>{{ sfdp.bsEraseSizes }}</td>
            </tr>
            <tr>
              <th>Write Enable Select</th>
              <td class="pl-2 pr-3">{{ sfdp.writeEnableOpcode ? '0x06' : '0x50' }}</td>
              <th class="border-l border-grey border-opacity-25 pr-2 pl-3">Density (bits)</th>
              <td>{{ sfdp.density?.toString() }}</td>
            </tr>
          </tbody>
        </table>
      </details>
    </div>
    </InfoBox>
</template>

<script>
import { formatMemorySize } from "../flash/util"
import InfoBox from "./InfoBox.vue";

export default {
  components: {
      InfoBox,
  },
  props: {
    sfdp: {
      type: Object,
      required: true
    }
  },
  computed: {
    hasAnyFeature() {
      const b = this.sfdp.bfpt;
      if (!b) return false;
      return b.supportDTR || b.support112FastRead || b.support122FastRead || 
             b.support114FastRead || b.support144FastRead ||
             (this.sfdp.hasOctalDDRSupport && this.sfdp.hasOctalDDRSupport()) ||
             (this.sfdp.hasXSPIProfile && this.sfdp.hasXSPIProfile());
    }
  },
  methods: {
    formatHex(value, pad = 2) {
      if (value === undefined || value === null) return '??';
      return value.toString(16).toUpperCase().padStart(pad, '0');
    },
    formatSignature(sig) {
      if (!sig) return 'N/A';
      // Convert 32-bit LE integer back to string
      const bytes = [
        sig & 0xFF,
        (sig >> 8) & 0xFF,
        (sig >> 16) & 0xFF,
        (sig >> 24) & 0xFF
      ];
      return String.fromCharCode(...bytes);
    },
    formatDensity(bits) {
      if (!bits) return 'Unknown';
      const bytes = Number(BigInt(bits) / 8n);
      return formatMemorySize(bytes);
    },
    shortenName(name) {
      // Shorten long parameter table names for display
      const maxLen = 30;
      if (name.length <= maxLen) return name;
      return name.substring(0, maxLen - 3) + '...';
    }
  }
};
</script>

<style scoped>
td, th {
  text-align: left;
  padding-top: 0.25rem;
  padding-bottom: 0.25rem;
}

.badge {
  @apply px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded;
}

details summary::-webkit-details-marker {
  display: none;
}

details summary::before {
  content: '▶ ';
  font-size: 0.75rem;
}

details[open] summary::before {
  content: '▼ ';
}
</style>
